import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Sidebar from "../Sidebar/Sidebar";
import OrderInformation from "../OrderInformation/OrderInformation";
import WarningList from "../Sidebar/WarningList/WarningList";
import PickActions from "../PickActions/PickActions";
import ConfirmBoxButton from "../buttons/ConfirmBoxButton";
import SwapBoxButton from "../buttons/SwapBoxButton";
import ChangeWeightButton from "../buttons/ChangeWeightButton";
import PostPickButton from "../buttons/PostPickButton";
import BatchPickButton from "../buttons/BatchPickButton";
import {
  fetchConveyorBox,
  setMissingBoxItem,
} from "../../../lib/store/reducers/conveyorBoxReviewReducer";
import { PayloadWrapper } from "../../../lib/store/types/PayloadWrapper";
import BoxItemList from "../BoxItemList/BoxItemList";
import PartialDeliveryButton from "../buttons/PartialDeliveryButton";
import {
  IConveyorBoxItemUI,
  IConveyorBoxUI,
  NumericKeys,
} from "../../../lib/interfaces/ConveyorBox/IConveyorBox";
import RelocateBoxButton from "../buttons/RelocateBoxButton";
import CancelOrderButton from "../buttons/CancelOrderButton";
import { confirmExcessPickThunk } from "../../../lib/store/thunks/conveyorBoxThunks";
import AutoVerifyAllItemsButton from "../AutoVerifyAllItemsButton";
import ConfirmVerifyPickFinishedButton from "../buttons/ConfirmVerifyPickFinishedButton";
import { AppDispatch, RootState } from "../../../lib/store/storeConfig";
import Footer from "../Footer/Footer";
import { controlAppStateSelector } from "../../../lib/store/reducers/controlAppStateReducer";
import AFrameConstants from "../../../lib/aframeboxlookup/Constants";
import useVerifyItemsA2 from "../../../hooks/useVerifyItems/useVerifyItemsA2";
import BarcodeScanner from "../BarcodeInput/BarcodeScanner";
import { useBarcodeScanner } from "../../../hooks/useBarcodeScanner";
import { LoadingState } from "../../../lib/store/reducers/loadingStateReducer";
const { LookupValueOrigin } = AFrameConstants;

export interface IAdjustedPickedQuantityActions {
  onIncreaseAdjustedPick: (itemId: number) => void;
  onDecreaseAdjustedPick: (itemId: number) => void;
}

export type AdjustedPickedQuantityProperty = {
  adjustedPickedQuantity: number;
};

export type PickProgressData = {
  itemId: number;
  pickedQuantity: number;
  confirmedQuantity: number;
  remainingQuantity: number;
  verifiedQuantity: number;
} & AdjustedPickedQuantityProperty;

export type PickProgressActions = {
  onConfirmExcessPicks: (itemId: number) => void;
  onAutoVerifyQuantity: (itemId: number) => void;
} & IAdjustedPickedQuantityActions;

export type IAdjustedPickedQuantity = AdjustedPickedQuantityProperty &
  IAdjustedPickedQuantityActions;
const OrderOverview = () => {
  const dispatch = useDispatch<AppDispatch>();

  const appState = useSelector(controlAppStateSelector);
  const conveyorBoxReview: IConveyorBoxUI = useSelector(
    (state: any) => state.conveyorBoxReviewReducer
  );
  const loadingState = useSelector<any, LoadingState>((state) => state.loadingStateReducer);

  const updateQuantitativeProperty = (
    id: number,
    adjustment: number,
    propertyName: NumericKeys<IConveyorBoxItemUI>
  ) => {
    const items = [...conveyorBoxReview.items.active];
    const itemIndex = items.findIndex((item) => item.id === id);
    if (itemIndex === -1) return;

    const updatedItem = { ...items[itemIndex] };
    updatedItem[propertyName] = (updatedItem[propertyName] ?? 0) + adjustment;
    if (updatedItem[propertyName] < 0 || updatedItem[propertyName] > updatedItem.confirmedQuantity)
      return;

    items.splice(itemIndex, 1, updatedItem);
    dispatch(setMissingBoxItem(new PayloadWrapper(items)));
  };

  const onIncreaseAdjustedPick = (id: number) =>
    updateQuantitativeProperty(id, 1, "adjustedPickedQuantity");
  const onDecreaseAdjustedPick = (id: number) =>
    updateQuantitativeProperty(id, -1, "adjustedPickedQuantity");

  const isEnoughStockQuantity = useMemo(() => {
    if (conveyorBoxReview?.items?.active?.length < 1) return false;
    return conveyorBoxReview.items.active
      .filter(
        (item) =>
          item.adjustedPickedQuantity < item.pickedQuantity &&
          item.pickedQuantity > 0 &&
          item.sku?.stock?.totalQuantity > 0
      )
      .some(
        (item) =>
          item.sku?.stock?.totalQuantity >= item.pickedQuantity - item.adjustedPickedQuantity
      );
  }, [conveyorBoxReview.stock, conveyorBoxReview.items.active]);

  const hasSelectedMissingItems = useMemo(
    () =>
      conveyorBoxReview.items.active.some(
        (item: any) => item.adjustedPickedQuantity < item.pickedQuantity
      ),
    [conveyorBoxReview.items]
  );

  const onConfirmExcessPicksHandler = async (itemId: number) => {
    await dispatch(confirmExcessPickThunk(itemId));
  };

  const items = conveyorBoxReview.items.active.sort((a, b) => {
    if (a.orderSkuID !== b.orderSkuID) {
      return a.orderSkuID - b.orderSkuID; // group by orderSkuID
    }
    return a.id - b.id;
  });

  const { onVerifyItemByBarcode, onVerifyRxBagByBarcode } = useVerifyItemsA2({
    items: items,
  });
  const { onExecuteHigherOrderHandlerOrDefault } = useBarcodeScanner();
  return (
    <>
      <main className="control-app__content">
        <BoxItemList
          items={items}
          actions={{
            onIncreaseAdjustedPick,
            onDecreaseAdjustedPick,
            onConfirmExcessPicks: onConfirmExcessPicksHandler,
          }}
          isLoadingStock={loadingState.isFetchingStockData}
        />
      </main>
      <Sidebar show={conveyorBoxReview.orderNumber !== -1}>
        <OrderInformation />
        <WarningList />
        <PickActions>
          <ConfirmVerifyPickFinishedButton />
          <AutoVerifyAllItemsButton />
          {/* Batch pick isnt being used */}
          {/* {isEnoughStockQuantity && hasSelectedMissingItems && <BatchPickButton />} */}
          {isEnoughStockQuantity && hasSelectedMissingItems && <PostPickButton />}
          <ChangeWeightButton />
          <SwapBoxButton />
          <RelocateBoxButton />
          {conveyorBoxReview.items.active.some((item) => item.pickedQuantity > 0) && (
            <PartialDeliveryButton />
          )}
          {!hasSelectedMissingItems && <ConfirmBoxButton />}
          <CancelOrderButton />
        </PickActions>
      </Sidebar>
      <BarcodeScanner
        onScan={async (value, format) => {
          return onExecuteHigherOrderHandlerOrDefault(value, format, () => {
            if (appState.isForcedRxBagVerification && !appState.isForcedRxBagVerificationFinished) {
              onVerifyRxBagByBarcode(value);
            } else {
              //format.includes("EAN")
              onVerifyItemByBarcode(value);
            }
          });
        }}
      />
    </>
  );
};

export default OrderOverview;
